import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Multiselect from './Multiselect.component';
import { i18nService } from '../../../services/i18n.service';

interface MultiselectContainerProps {
	onChange: (obj: {
		name: string;
		value: Array<any>;
		isMulti: true;
		isAllSelected?: boolean;
	}) => void;
	config: {
		type?: string;
		name: string;
		options: { name: string; id: string | number; stringId?: string }[];
		placeholder?: string;
		styles?: any;
		defaultLabel?: string;
		value?: any;
		key?: string;
		alwaysRerender: boolean;
		forceNoAll?: boolean;
		noSpecialAll: boolean;
		dashboardViewMode?: boolean;
	};
	styles?: any;
	noSelection?: boolean;
	noSpecialAll?: boolean;
	menuIsOpenDefault?: boolean;
	resetActiveSortByProps?: [];
}

interface SelectedOptions {
	label: string;
	value: string;
}

const convertOption = (val: any) => ({
	label: val?.name,
	value: val?.id,
});

const MultiselectContainer = ({
	onChange,
	config,
	resetActiveSortByProps = [],
	...restProps
}: MultiselectContainerProps) => {
	const [showList, setShowList] = useState(false);
	const allOption = useMemo(
		() => ({
			value: 'all',
			label: config?.defaultLabel || i18nService.translate('general.all'),
		}),
		[config],
	);
	const [selectedOptions, setSelectedOptions] = useState<SelectedOptions[]>(
		config?.value?.map(convertOption) || [],
	);

	useEffect(() => {
		setSelectedOptions([]);
	}, resetActiveSortByProps);

	useEffect(() => {
		if (
			!selectedOptions.find((opt: any) => opt.value === 'all') ||
			config.alwaysRerender
		) {
			if (config?.value) {
				const values = config.value.map(convertOption);
				//keep "All" checked when every option is selected
				const isAllValues =
					!config.forceNoAll &&
					values.length > 0 &&
					values.length === config.options?.length;
				setSelectedOptions(isAllValues ? [allOption, ...values] : values);
			} else {
				setSelectedOptions([]);
			}
		}
	}, [config.alwaysRerender, config.key, config.value]);

	const selectionsChangeHandler = (selections: any, item: any) => {
		let tempSelectedOptions: any = [];
		let isAllSelected = false;
		const isSelectionsContainAllOpt =
			selectedOptions.findIndex((item: any) => item.value === 'all') >= 0;

		//select all on All option click
		if (
			!isSelectionsContainAllOpt &&
			selections.some((v: any) => v?.value === 'all')
		) {
			const allOptionSelected = config.options.map((v: any) => {
				return {
					value: v?.id,
					label: v?.name,
				};
			});
			isAllSelected = true;
			tempSelectedOptions = [allOption, ...allOptionSelected];
			onShowHideListHandler();
			//unselect all on All option click
		} else if (
			isSelectionsContainAllOpt &&
			!selections.some((v: any) => v?.value === 'all')
		) {
			tempSelectedOptions = [];
		} else {
			tempSelectedOptions = selections.filter(
				(sel: any) => sel.value !== 'all',
			);
			if (tempSelectedOptions.length === config.options.length) {
				//if all options was selected than choose also "All" option
				if (!config.forceNoAll) {
					tempSelectedOptions.unshift(allOption);
				}
				isAllSelected = true;
			}
		}
		setSelectedOptions(tempSelectedOptions);
		onChange({
			name: config.name,
			value: transformSelectedOptions(tempSelectedOptions),
			isMulti: true,
			isAllSelected,
		});
	};
	const transformSelectedOptions = (arr: SelectedOptions[]) => {
		return arr.find(opt => opt.value === 'all') &&
			!restProps.noSpecialAll &&
			!config.noSpecialAll
			? []
			: arr.map(selectedOption => {
					return {
						id: selectedOption.value,
						name: selectedOption.label,
					};
			  });
	};

	const onShowHideListHandler = useCallback(() => {
		setShowList(!showList);
	}, [showList]);

	const getAllOptions = () => {
		if (!config.options) {
			return;
		}
		let optionsCustom = config.options.map(
			(opt: { name: string; id: string }) => {
				return {
					label: opt.name,
					value: opt.id,
				};
			},
		);
		if (optionsCustom.length && !config.forceNoAll) {
			optionsCustom.unshift(allOption);
		}
		return optionsCustom;
	};

	return (
		<Multiselect
			showList={showList}
			selectedOptions={selectedOptions}
			config={{ ...config, options: getAllOptions() }}
			onChangeCheckBox={selectionsChangeHandler}
			onShowHideList={onShowHideListHandler}
			{...restProps}
		/>
	);
};

export default MultiselectContainer;
